/**
 * 管理后台 · 录像管理与点播入口。
 * 替代老系统 man_recordfile.jsp / editrecordfile.jsp / pubrecord.jsp。
 */

import { useCallback, useEffect, useState } from 'react'

import {
  adminApi,
  formatDuration,
  formatSize,
  formatTime,
  type RecordRow,
  type RoomRow,
} from '../../lib/adminApi'
import { assetURL } from '../../lib/endpoint'

interface Props {
  token: string
  onError: (msg: string) => void
  onNotice: (msg: string) => void
  onPlay: (r: RecordRow) => void
}

export function RecordsPanel({ token, onError, onNotice, onPlay }: Props) {
  const [rooms, setRooms] = useState<RoomRow[]>([])
  const [roomId, setRoomId] = useState<number | null>(null)
  const [records, setRecords] = useState<RecordRow[]>([])
  const [busy, setBusy] = useState(false)

  // 先取房间列表，供选择。
  useEffect(() => {
    ;(async () => {
      try {
        const p = await adminApi.listRooms(token, '', 200, 0)
        const list = p.items ?? []
        setRooms(list)
        if (list.length > 0 && roomId === null) setRoomId(list[0]!.id)
      } catch (e) {
        onError(e instanceof Error ? e.message : '加载房间失败')
      }
    })()
    // roomId 故意不进依赖：只在首次挂载时选中第一个房间。
  }, [token, onError])

  const load = useCallback(async () => {
    if (roomId === null) return
    setBusy(true)
    try {
      const d = await adminApi.listRecords(token, roomId)
      setRecords(d.records ?? [])
    } catch (e) {
      onError(e instanceof Error ? e.message : '加载录像失败')
    } finally {
      setBusy(false)
    }
  }, [token, roomId, onError])

  useEffect(() => {
    void load()
  }, [load])

  const remove = async (r: RecordRow) => {
    if (!confirm(`确定从列表中移除录像「${r.title || r.recordId}」吗？\n文件本身会保留在磁盘上。`)) {
      return
    }
    try {
      await adminApi.deleteRecord(token, r.recordId)
      onNotice('已从列表移除（文件保留）')
      void load()
    } catch (e) {
      onError(e instanceof Error ? e.message : '删除失败')
    }
  }

  return (
    <div className="adm-panel">
      <div className="adm-bar">
        <select
          value={roomId ?? ''}
          onChange={(e) => setRoomId(Number(e.target.value))}
        >
          {rooms.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name || '未命名'}（{r.id}）
            </option>
          ))}
          {rooms.length === 0 && <option value="">暂无房间</option>}
        </select>
        <button onClick={() => void load()} disabled={busy}>
          {busy ? '刷新中…' : '刷新'}
        </button>
        <span className="adm-count">共 {records.length} 个录像</span>
      </div>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>标题</th>
              <th>录制时间</th>
              <th>时长</th>
              <th>大小</th>
              <th>录制人</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.recordId}>
                <td>{r.title || <span className="dim">未命名</span>}</td>
                <td className="dim">{formatTime(r.beginTime)}</td>
                <td>{formatDuration(r.duration)}</td>
                <td className="dim">{formatSize(r.size)}</td>
                <td>{r.owner || '—'}</td>
                <td className="adm-ops">
                  <button className="mini primary" onClick={() => onPlay(r)}>
                    回放
                  </button>
                  <a className="mini-link" href={assetURL(r.url)} download>
                    下载
                  </a>
                  <button className="mini danger" onClick={() => void remove(r)}>
                    移除
                  </button>
                </td>
              </tr>
            ))}
            {records.length === 0 && !busy && (
              <tr>
                <td colSpan={6} className="adm-empty">
                  该房间还没有录像
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="adm-hint">
        录制结束即自动登记，不必再手工补录。移除只删列表条目，磁盘文件保留，
        便于误删后恢复。
      </p>
    </div>
  )
}
